import { FormEvent, useCallback, useEffect, useState } from "react";
import { api, payload } from "./lib/api";

type Result = "present" | "absent" | "unknown";

interface Rule { key: string; area: string; label: string; weight: number; fetchable: boolean }
interface Evidence {
  id: string; area: string; ruleKey: string; kind: "fact" | "inference"; result: Result; observation: string;
  excerpt: string | null; sourceUrl: string; capturedAt: string; method: string;
  fetchSource: "raw_html" | "rendered_dom" | "manual"; internalOnly: boolean;
}
interface Competitor {
  id: string; name: string; url: string; selectionMethod: string; selectionNote: string;
  observations: Record<string, { result: Result; sourceUrl: string; capturedAt: string }>;
}
interface Qualification {
  id: string; score: number; outcome: string; reasons: string[]; finalOutcome: string | null; finalNote: string | null;
  gaps: Array<{ ruleKey: string; area: string; weight: number; evidenceIds: string[]; backedBy: string[] }>;
}
interface Message { id: string; subject: string; gmail_draft_id: string | null; draft_state: string | null; created_at: string }
interface Review {
  profile: string; profileLabel: string;
  rules: Rule[]; rubricVersion: string; provenanceComplete: boolean; digitalIdentity: string; canDraft: boolean; status: string;
  browserRun: BrowserRunStatus | null;
  evidence: Evidence[]; competitors: Competitor[]; qualification: Qualification | null; messages: Message[];
}
interface GmailStatus { configured: boolean; connected: boolean; account: string; sendingEnabled: boolean; signatureSet: boolean; senderSignature: string }
interface BrowserRunStatus {
  enabled: boolean; dailyUsedMs: number; dailyLimitMs: number; monthlyUsedMs: number; monthlyLimitMs: number; blockedReason: string | null;
}

const AREAS: Record<string, string> = { visibility: "Visibilità", offer: "Offerta", trust: "Fiducia", conversion: "Conversione", technical: "Tecnica (solo interna)" };
const RESULT_LABEL: Record<Result, string> = { present: "Presente", absent: "Assente", unknown: "Non noto" };
const OUTCOME_LABEL: Record<string, string> = { qualified: "Qualificato", needs_review: "Da rivedere", rejected: "Rifiutato" };
const FETCH_SOURCE_LABEL: Record<Evidence["fetchSource"], string> = {
  raw_html: "HTML ricevuto", rendered_dom: "pagina renderizzata", manual: "verifica manuale",
};
const minutes = (milliseconds: number) => (milliseconds / 60_000).toLocaleString("it-IT", { maximumFractionDigits: 1 });

function describe(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Operazione non riuscita";
}

export function LeadReview({ leadId, hasEmail, onChanged }: { leadId: string; hasEmail: boolean; onChanged: () => void }) {
  const [review, setReview] = useState<Review | null>(null);
  const [gmail, setGmail] = useState<GmailStatus | null>(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api<Review>(`/api/leads/${leadId}/review`).then(setReview).catch((reason) => setError(describe(reason)));
    api<GmailStatus>("/api/leads/gmail/status").then(setGmail).catch(() => undefined);
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  async function run(label: string, action: () => Promise<unknown>) {
    setBusy(label);
    setError("");
    setNote("");
    try {
      await action();
      load();
      onChanged();
    } catch (reason) {
      setError(describe(reason));
    } finally {
      setBusy("");
    }
  }

  if (!review) return <section><h2>Revisione</h2>{error ? <p className="leads-error">{error}</p> : <p className="leads-note">Caricamento…</p>}</section>;
  const ruleLabel = (key: string) => review.rules.find((rule) => rule.key === key)?.label || key;
  const visible = review.evidence.filter((item) => !item.internalOnly);
  const internal = review.evidence.filter((item) => item.internalOnly);

  return (
    <>
      <section>
        <h2>Prove</h2>
        <p className="leads-note">Profilo di settore: <strong>{review.profileLabel}</strong> ({review.rubricVersion})</p>
        {review.browserRun && (
          <p className={review.browserRun.blockedReason ? "leads-error" : "leads-note"}>
            Browser Run: {minutes(review.browserRun.dailyUsedMs)}/{minutes(review.browserRun.dailyLimitMs)} min oggi, {minutes(review.browserRun.monthlyUsedMs)}/{minutes(review.browserRun.monthlyLimitMs)} min nel mese (UTC).
            {review.browserRun.blockedReason ? ` Bloccato: ${review.browserRun.blockedReason}.` : ""}
          </p>
        )}
        {!review.provenanceComplete && <p className="leads-error">Provenienza incompleta: l’analisi è bloccata.</p>}
        {error && <p className="leads-error">{error}</p>}
        {note && <p className="leads-note">{note}</p>}
        <button className="secondary" disabled={!review.provenanceComplete || busy !== ""} onClick={() => run("audit", async () => {
          const result = await api<{ status: string; error: string | null; evidence: number; fetchSource: Evidence["fetchSource"] }>(`/api/leads/${leadId}/audit`, payload("POST", {}));
          const source = FETCH_SOURCE_LABEL[result.fetchSource];
          setNote(result.status === "failed" ? `Sito non scaricabile: ${result.error}. Le regole risultano «Non noto».` : `Analisi completata: ${result.evidence} osservazioni da ${source}.`);
        })}>{busy === "audit" ? "Analizzo… (alcuni secondi)" : review.digitalIdentity === "HAS_WEBSITE" ? "Analizza il sito" : "Registra assenza di sito"}</button>
        {visible.length === 0 ? <p className="leads-note">Nessuna prova ancora.</p> : (
          <div className="leads-table">
            {Object.keys(AREAS).filter((area) => area !== "technical").map((area) => {
              const items = visible.filter((item) => item.area === area);
              if (!items.length) return null;
              return (
                <div key={area}>
                  <h3>{AREAS[area]}</h3>
                  <ul>
                    {items.map((item) => (
                      <li key={item.id}>
                        <strong>{ruleLabel(item.ruleKey)}</strong>: {RESULT_LABEL[item.result]} · {item.kind === "fact" ? "FATTO" : "INFERENZA"} · {item.method === "manual" ? "manuale" : "automatica"}
                        <br />{item.observation}
                        <br /><small><a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">{item.sourceUrl}</a> · {item.capturedAt.slice(0, 10)} · {FETCH_SOURCE_LABEL[item.fetchSource] || "sorgente non indicata"}</small>
                        {item.excerpt && <><br /><small>«{item.excerpt}»</small></>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {internal.length > 0 && (
              <details>
                <summary>Dettagli tecnici (uso interno, mai nel messaggio)</summary>
                <ul>{internal.map((item) => <li key={item.id}>{ruleLabel(item.ruleKey)}: {RESULT_LABEL[item.result]}</li>)}</ul>
              </details>
            )}
          </div>
        )}
        <EvidenceForm rules={review.rules.filter((rule) => rule.area !== "technical")} onSubmit={(body) => run("evidence", () => api(`/api/leads/${leadId}/evidence`, payload("POST", body)))} />
      </section>

      <section>
        <h2>Concorrenti</h2>
        <p className="leads-note">Servono almeno due concorrenti di Pesaro con sito proprio. Una lacuna conta solo se il lead non ha la funzione e almeno due concorrenti sì.</p>
        {review.competitors.map((competitor) => (
          <div key={competitor.id} className="leads-note">
            <strong>{competitor.name}</strong> · <a href={competitor.url} target="_blank" rel="noopener noreferrer">{competitor.url}</a>
            <br /><small>{competitor.selectionMethod === "same_batch" ? "Dallo stesso lotto" : "Ricerca locale manuale"}: {competitor.selectionNote}</small>
            <br />
            <button className="secondary" disabled={busy !== ""} onClick={() => run("comp", async () => {
              const result = await api<{ status: string; error?: string; fetchSource: Evidence["fetchSource"] }>(`/api/leads/${leadId}/competitors/${competitor.id}/audit`, payload("POST", {}));
              if (result.status === "failed") setNote(`Sito del concorrente non scaricabile: ${result.error}`);
              else setNote(`Concorrente analizzato da ${FETCH_SOURCE_LABEL[result.fetchSource]}.`);
            })}>{busy === "comp" ? "Analizzo…" : "Analizza il sito del concorrente"}</button>
            {" "}
            <button className="secondary" disabled={busy !== ""} onClick={() => {
              const name = window.prompt("Nome del concorrente come lo scriveresti in un'email (es. «Hotel Nettuno», non il titolo del sito):", competitor.name);
              if (!name || name.trim() === competitor.name) return;
              run("rename", async () => {
                await api(`/api/leads/${leadId}/competitors/${competitor.id}`, payload("PATCH", { name: name.trim() }));
                setNote(`Concorrente rinominato in «${name.trim()}».`);
              });
            }}>{busy === "rename" ? "Salvo…" : "Rinomina"}</button>
            <br /><small>{Object.keys(competitor.observations).filter((key) => !key.startsWith("X_")).map((key) => `${ruleLabel(key)}: ${RESULT_LABEL[competitor.observations[key].result]}`).join(" · ") || "Nessuna osservazione"}</small>
          </div>
        ))}
        <CompetitorForm onSubmit={(body) => run("addcomp", () => api(`/api/leads/${leadId}/competitors`, payload("POST", body)))} />
      </section>

      <section>
        <h2>Esito</h2>
        <button className="secondary" disabled={busy !== ""} onClick={() => run("qualify", () => api(`/api/leads/${leadId}/qualify`, payload("POST", {})))}>{busy === "qualify" ? "Calcolo…" : "Calcola l’esito proposto"}</button>
        {review.qualification && (
          <>
            <p><strong>Universe Score {review.qualification.score}</strong> · proposta: {OUTCOME_LABEL[review.qualification.outcome]} ({review.rubricVersion})</p>
            <ul>{review.qualification.reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
            {review.qualification.finalOutcome
              ? <p className="leads-note">Decisione: <strong>{OUTCOME_LABEL[review.qualification.finalOutcome]}</strong>{review.qualification.finalNote ? ` · ${review.qualification.finalNote}` : ""}</p>
              : <DecisionForm canQualify={review.qualification.outcome === "qualified"} onSubmit={(body) => run("decision", () => api(`/api/leads/${leadId}/decision`, payload("POST", body)))} />}
          </>
        )}
      </section>

      <DraftSection leadId={leadId} review={review} gmail={gmail} hasEmail={hasEmail} busy={busy} run={run} reload={load} />
    </>
  );
}

function EvidenceForm({ rules, onSubmit }: { rules: Rule[]; onSubmit: (body: Record<string, string>) => void }) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(Object.fromEntries(new FormData(event.currentTarget).entries()) as Record<string, string>);
    event.currentTarget.reset();
  }
  return (
    <details>
      <summary>Aggiungi una prova manuale</summary>
      <form className="leads-form" onSubmit={submit}>
        <label>Regola<select name="ruleKey">{rules.map((rule) => <option key={rule.key} value={rule.key}>{rule.label}</option>)}</select></label>
        <label>Esito
          <select name="result"><option value="absent">Assente</option><option value="present">Presente</option><option value="unknown">Non noto</option></select>
        </label>
        <label>Cosa hai osservato<input name="observation" required /></label>
        <label>URL dove l’hai visto<input name="sourceUrl" type="url" required /></label>
        <label>Estratto (facoltativo)<input name="excerpt" maxLength={300} /></label>
        <button className="secondary">Salva prova</button>
      </form>
    </details>
  );
}

function CompetitorForm({ onSubmit }: { onSubmit: (body: Record<string, string>) => void }) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(Object.fromEntries(new FormData(event.currentTarget).entries()) as Record<string, string>);
    event.currentTarget.reset();
  }
  return (
    <details>
      <summary>Aggiungi un concorrente</summary>
      <form className="leads-form" onSubmit={submit}>
        <label>Nome<input name="name" required /></label>
        <label>Sito<input name="url" type="url" required /></label>
        <label>Come l’hai scelto
          <select name="selectionMethod"><option value="same_batch">Dallo stesso lotto</option><option value="manual_local_search">Ricerca locale manuale</option></select>
        </label>
        <label>Perché è un concorrente comparabile<input name="selectionNote" required /></label>
        <button className="secondary">Aggiungi</button>
      </form>
    </details>
  );
}

function DecisionForm({ canQualify, onSubmit }: { canQualify: boolean; onSubmit: (body: Record<string, unknown>) => void }) {
  const [text, setText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="leads-form">
      <label>Nota (obbligatoria se rifiuti)<input value={text} onChange={(event) => setText(event.target.value)} /></label>
      <label><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> Ho verificato che l’attività è aperta e indipendente (non una catena)</label>
      <button className="primary" disabled={!canQualify} onClick={() => onSubmit({ finalOutcome: "qualified", note: text, independentActiveConfirmed: confirmed })}>Approva come qualificato</button>
      <button className="secondary" onClick={() => onSubmit({ finalOutcome: "rejected", note: text })}>Rifiuta</button>
      <button className="secondary" onClick={() => onSubmit({ finalOutcome: "needs_review", note: text })}>Servono altre prove</button>
    </div>
  );
}

function DraftSection({ leadId, review, gmail, hasEmail, busy, run, reload }: {
  leadId: string; review: Review; gmail: GmailStatus | null; hasEmail: boolean; busy: string;
  run: (label: string, action: () => Promise<unknown>) => Promise<void>; reload: () => void;
}) {
  const [operatorNote, setOperatorNote] = useState("");
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);
  const [signature, setSignature] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => { if (gmail) setSignature(gmail.senderSignature); }, [gmail]);
  const approved = review.qualification?.finalOutcome === "qualified";
  if (!approved) return null;
  const existing = review.messages.find((item) => item.gmail_draft_id);

  return (
    <section>
      <h2>Bozza Gmail</h2>
      {!review.canDraft && <p className="leads-error">{hasEmail ? "L’email non è segnata come pubblica sul sito dell’attività" : "Nessuna email"}: niente bozza Gmail. Contatto manuale.</p>}
      {gmail && !gmail.connected && (
        <p className="leads-note">
          Gmail non collegato.{" "}
          {gmail.configured
            ? <button className="secondary" onClick={() => run("gmail", async () => { const result = await api<{ authorizationUrl: string }>("/api/leads/gmail/connect", payload("POST", {})); window.location.href = result.authorizationUrl; })}>Collega {gmail.account}</button>
            : "Mancano le chiavi Google sul server (GMAIL_CLIENT_ID e GMAIL_CLIENT_SECRET)."}
        </p>
      )}
      {gmail?.connected && <p className="leads-note">Collegato a {gmail.account}. Il sistema crea solo bozze: l’invio lo fai tu da Gmail.</p>}
      <label className="leads-form">Firma da usare nei messaggi
        <textarea rows={3} value={signature} onChange={(event) => setSignature(event.target.value)} />
        <button className="secondary" onClick={() => run("sig", () => api("/api/leads/settings", payload("PATCH", { senderSignature: signature })))}>Salva firma</button>
      </label>
      {existing ? (
        <p className="leads-note">Bozza creata in Gmail (id {existing.gmail_draft_id}). Aprila in Gmail, rileggila e inviala tu.{existing.draft_state === "check_manually" ? " Attenzione: verifica a mano." : ""}</p>
      ) : (
        <div className="leads-form">
          <label>Apertura personale (facoltativa)<textarea rows={2} value={operatorNote} onChange={(event) => setOperatorNote(event.target.value)} /></label>
          <button className="secondary" disabled={busy !== "" || !review.canDraft} onClick={() => run("preview", async () => {
            try { setPreview(await api(`/api/leads/${leadId}/draft/preview`, payload("POST", { operatorNote }))); setMessage(""); } catch (reason) { setPreview(null); setMessage(describe(reason)); }
          })}>Anteprima del testo</button>
          {message && <p className="leads-error">{message}</p>}
          {preview && (
            <>
              <p><strong>{preview.subject}</strong></p>
              <pre style={{ whiteSpace: "pre-wrap" }}>{preview.body}</pre>
              <button className="primary" disabled={busy !== "" || !gmail?.connected} onClick={() => run("draft", async () => {
                await api(`/api/leads/${leadId}/draft`, payload("POST", { operatorNote }));
                reload();
              })}>{busy === "draft" ? "Creo la bozza…" : "Crea bozza in Gmail"}</button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
